import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import NotificationSnackbar from "./NotificationBar";
function AddnewSchool({ open, onClose, onSubmit }) {
  const theme = useTheme();

  const [form, setForm] = useState({
    name: "",
    country: null,
  });

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  useEffect(() => {
    if (open) {
      fetchCountries();
    }
  }, [open]);

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const res = await fetch("/api/support/countries");
      const data = await res.json();
      const list = data.recordset || data;
      setCountries(list);
    } catch (error) {
      console.error("Failed to load countries:", error);
      setSnackbarMessage("Failed to load countries");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, name: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.country?.id) return;
    try {
      const res = await fetch("/api/support/schools", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: form.name,
          countryId: form.country.id,
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error("Server did not return valid JSON");
      }

      if (!res.ok) {
        throw new Error(data?.message || "Server error occurred");
      }

      setSnackbarMessage(data?.message || "School added successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setForm({ name: "", country: null });
      setTimeout(() => {
        onSubmit();
        onClose();
      }, 3000)

    } catch (error) {
      console.error("Submit error:", error);

      setSnackbarMessage(error.message || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": { borderRadius: 3 },
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: theme.palette.primary.main,
          color: "white",
          fontWeight: 600,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Add New School
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent dividers>
        <Grid container spacing={2} mt={1}>
          {/* School Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="School Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              autoFocus
              placeholder="e.g. University of Rwanda"
            />
          </Grid>

          {/* Searchable Country */}
          <Grid item xs={12}>
            <Autocomplete
              options={countries}
              loading={loadingCountries}
              value={form.country}
              onChange={(e, newValue) =>
                setForm({ ...form, country: newValue })
              }
              getOptionLabel={(option) => option?.Country || ""}
              isOptionEqualToValue={(option, value) =>
                option?.id === value?.id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  placeholder="Type to search country..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCountries ? (
                          <CircularProgress size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!form.name || !form.country}
        >
          Save School
        </Button>
      </DialogActions>

      {/* Snackbar */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default AddnewSchool;
